
import { Vue, Options } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { PREFECTURES } from '@/utils/constants'
import { IDynamicLiffFormField } from '@/utils/types'

@Options({
  components: {},
  emits: ['update:modelValue', 'submit', 'reset'],
})
export default class FormPreview extends Vue {
  @Prop({ default: () => [] })
  formFields!: IDynamicLiffFormField[]

  @Prop({ default: '' })
  formTitle!: string

  @Prop({ default: false })
  readonly!: boolean

  @Prop({ default: true })
  showTitle!: boolean

  @Prop({ default: false })
  showButtons!: boolean

  @Prop({ default: false })
  submitting!: boolean

  @Prop({ default: () => ({}) })
  modelValue!: Record<string, any>

  @Prop()
  formImageHeaderUrl!: string

  formData: Record<string, any> = {}
  previewData: Record<string, any> = {} // Use field ID as key instead of index
  fieldKeyMap: Map<any, string> = new Map() // Map each field reference to its unique key
  nextUniqueId = 0 // Counter for generating unique IDs
  isInitializing = false
  isUpdatingFromParent = false

  get prefectureOptions() {
    return PREFECTURES
  }

  // Get unique key for field - use _id if available, otherwise generate stable unique key
  getFieldKey(field: IDynamicLiffFormField): string {
    const existingKey = this.fieldKeyMap.get(field)
    if (existingKey) {
      return existingKey
    }

    // Generate new key: prefer _id, otherwise create unique key
    let key: string
    if (field._id) {
      key = field._id
    } else {
      // Create a truly unique key that won't collide
      key = `field_${this.nextUniqueId++}_${Date.now()}`
    }

    this.fieldKeyMap.set(field, key)
    return key
  }

  @Watch('formFields', { immediate: true, deep: true })
  onFormFieldsChange() {
    this.isInitializing = true
    this.initializeFormData()
    this.initializePreviewData()
    this.$nextTick(() => {
      this.isInitializing = false
    })
  }

  @Watch('modelValue', { immediate: true, deep: true })
  onModelValueChange() {
    if (this.modelValue && !this.isInitializing) {
      this.isUpdatingFromParent = true
      this.formData = { ...this.modelValue }
      const existingPreviewData = { ...this.previewData }
      this.initializePreviewData(existingPreviewData)
      this.$nextTick(() => {
        this.isUpdatingFromParent = false
      })
    }
  }

  @Watch('previewData', { deep: true })
  onPreviewDataChange() {
    if (!this.isInitializing && !this.isUpdatingFromParent && this.modelValue !== undefined) {
      // Only emit updates if modelValue is provided (submission mode) and not updating from parent
      this.formFields.forEach((field) => {
        const fieldKey = this.getFieldKey(field)
        this.formData[fieldKey] = this.previewData[fieldKey]
      })

      this.$emit('update:modelValue', this.formData)
    }
  }

  initializeFormData() {
    if (!this.formFields || this.formFields.length === 0) {
      this.formData = {}
      return
    }

    // Initialize form data with default values based on field types
    const newFormData = this.formFields.reduce((data, field) => {
      const fieldKey = this.getFieldKey(field)
      const existingValue = this.formData[fieldKey]

      if (existingValue !== undefined) {
        data[fieldKey] = existingValue
      } else {
        switch (field.option_type) {
          case 'checkbox':
            data[fieldKey] = []
            break
          case 'select':
          case 'radio':
          case 'prefecture':
            data[fieldKey] = ''
            break
          default:
            data[fieldKey] = ''
        }
      }
      return data
    }, {} as Record<string, any>)

    this.formData = newFormData
  }

  initializePreviewData(existingData: Record<string, any> = {}) {
    if (!this.formFields || this.formFields.length === 0) {
      this.previewData = {}
      return
    }

    // Initialize preview data with default values, preserving existing user input
    const newPreviewData = this.formFields.reduce((data, field) => {
      const fieldKey = this.getFieldKey(field)

      // Use existing data if provided, otherwise preserve current preview data
      if (existingData[fieldKey] !== undefined) {
        data[fieldKey] = existingData[fieldKey]
      } else if (this.previewData[fieldKey] !== undefined) {
        data[fieldKey] = this.previewData[fieldKey]
      } else {
        // Set default value for new field
        switch (field.option_type) {
          case 'checkbox':
            data[fieldKey] = []
            break
          case 'select':
          case 'radio':
          case 'prefecture':
            data[fieldKey] = ''
            break
          default:
            data[fieldKey] = ''
        }
      }
      return data
    }, {} as Record<string, any>)

    this.previewData = newPreviewData
  }

  onSubmit() {
    // Clean the form data by removing index suffixes from radio/checkbox values
    const cleanedFormData = this.cleanFormData(this.formData)
    console.log('cleanedFormData', cleanedFormData)

    this.$emit('submit', cleanedFormData)
  }

  // Clean form data by removing index suffixes from option values
  cleanFormData(data: Record<string, any>): Record<string, any> {
    const cleaned = { ...data }

    this.formFields.forEach((field) => {
      const fieldKey = this.getFieldKey(field)
      const value = cleaned[fieldKey]

      if (value !== undefined && (field.option_type === 'radio' || field.option_type === 'checkbox')) {
        if (field.option_type === 'radio') {
          // For radio, remove the index suffix to get the original value
          if (typeof value === 'string' && value.includes('_')) {
            const lastUnderscoreIndex = value.lastIndexOf('_')
            const originalValue = value.substring(0, lastUnderscoreIndex)
            cleaned[fieldKey] = originalValue
          }
        } else if (field.option_type === 'checkbox') {
          // For checkbox, it's an array, so clean each value
          if (Array.isArray(value)) {
            cleaned[fieldKey] = value.map((val) => {
              if (typeof val === 'string' && val.includes('_')) {
                const lastUnderscoreIndex = val.lastIndexOf('_')
                return val.substring(0, lastUnderscoreIndex)
              }
              return val
            })
          }
        }
      }
    })

    return cleaned
  }

  onReset() {
    this.initializeFormData()
    this.initializePreviewData() // Reset doesn't preserve data
    this.$emit('reset')
  }
}
